import { log, hasKey } from "./utils";
import { getAllPlaceData, getReversedPlaceData } from "./mapPlannerShared";

/**
 * Main entry point: finds all route‐segments (primary and child) between two places,
 * marking each as onPath=true/false, taking into account any child‐routes in via[] that
 * the user “must” take.  Supports "forward" and "reverse" directions by first optionally
 * reversing the data, then always slicing “forward.”
 *
 * @param {number} routeId       – The ID of the primary route on which we’re traveling.
 * @param {string} direction     – Either 'forward' or 'reverse'.
 * @param {number} originId      – The ID of the place where the journey starts.
 * @param {number} destinationId – The ID of the place where the journey ends.
 * @param {Array<number>} via    – (Optional) array of child‐route IDs the user “must” take.
 *
 * @returns {Array<Object>|false}
 *   - If inputs are invalid (e.g. origin/destination are the same, or are intersections,
 *     or destination does not come after origin), returns false.
 *   - Otherwise returns an array of segment‐objects, each with:
 *       {
 *         routeId:        (number)   ID of the route this segment belongs to,
 *         onPath:         (boolean)  true if this segment is on the chosen path,
 *         distance:       (number)   meters traversed along this segment,
 *         ascent:         (number)   total ascent over the segment,
 *         descent:        (number)   total descent over the segment,
 *         places:         (Array<Object>)  full place‐objects in walking order
 *       }
 *
 *   The array is sorted in the order the user “encounters” them on the primary route.
 */
export function pathfinder(routeId, direction, originId, destinationId, via = []) {
    log(`pathfinder: routeId=${routeId}, direction=${direction}, originId=${originId}, destinationId=${destinationId}, via=[${via.join(',')}]`);
    // ────────────────────────────────────────────────────────────────────────────────
    // 1) Build a forward‐only “view” of the data, regardless of requested direction.
    //    If direction === 'reverse', we create a reversed copy with inverted
    //    dist/asc/desc so that every route.places[] now reads “forward” in the
    //    walking order.  Then all logic can assume "forward."
    // ────────────────────────────────────────────────────────────────────────────────
    const workingData = (direction === 'reverse')
        ? getReversedPlaceData()
        : getAllPlaceData();

    // ────────────────────────────────────────────────────────────────────────────────
    // 2) Build a quick lookup: routeId → (placeId → index in route.places).
    //    This lets us find any place’s index in O(1), instead of scanning.
    // ────────────────────────────────────────────────────────────────────────────────
    const routeToIndexMap = buildIndexMaps(workingData);

    // ────────────────────────────────────────────────────────────────────────────────
    // 3) Locate which route each of originId and destinationId belongs to.
    //    If either isn’t found, or is an intersection, or origin===destination,
    //    or destination does not come after origin, return false.
    //    NOTE: SHOULD ONLY SEARCH THE ROUTEID AND ITS CHILDREN.
    // ────────────────────────────────────────────────────────────────────────────────
    const originRouteId = findRouteOfPlace(workingData, routeId, routeToIndexMap, originId);
    const destRouteId   = findRouteOfPlace(workingData, routeId, routeToIndexMap, destinationId);

    if (originRouteId === null || destRouteId === null) {
      log('pathfinder: Origin or destination not found');
      return false;
    }

    // Neither origin nor destination may be an intersection:
    if (isIntersection(workingData, originRouteId, originId) ||
        isIntersection(workingData, destRouteId, destinationId)) {
      log('pathfinder: Neither origin nor destination may be an intersection');
      return false;
    }
    // They cannot be identical:
    if (originId === destinationId){
      log('pathfinder: Origin and destination cannot be the same');
      return false;
    }

    // If both are on the primary route, ensure destIndex > originIndex:
    if (originRouteId === routeId && destRouteId === routeId) {
      const oIdx = routeToIndexMap[routeId][originId];
      const dIdx = routeToIndexMap[routeId][destinationId];
      if (dIdx <= oIdx){
        log('pathfinder: Destination index must be greater than origin index');
        return false;
      }
    }

    // ────────────────────────────────────────────────────────────────────────────────
    // 4) Determine which “case” (1–5) applies:
    //    Check if origin/destination are on primary or on a child route.
    // ────────────────────────────────────────────────────────────────────────────────
    const primaryRoute   = workingData.routes[routeId];
    // Build a map of primary’s direct children for quick testing:
    const primaryChildren = Object.values(workingData.routes)
      .filter(r => r.parent === routeId)
      .reduce((acc, r) => { acc[r.id] = r; return acc; }, {});

    const originOnPrimary      = (originRouteId === routeId);
    const destinationOnPrimary = (destRouteId === routeId);
    const originIsChild        = (originRouteId !== routeId && primaryChildren[originRouteId]);
    const destIsChild          = (destRouteId !== routeId && primaryChildren[destRouteId]);

    let caseNumber = null;
    if (originOnPrimary && destinationOnPrimary) {
      caseNumber = 1;
    } else if (originIsChild && destinationOnPrimary) {
      caseNumber = 2;
    } else if (originOnPrimary && destIsChild) {
      caseNumber = 3;
    } else if (originIsChild && destIsChild && originRouteId !== destRouteId) {
      caseNumber = 4;
    } else if (originRouteId === destRouteId && originIsChild && destIsChild) {
      caseNumber = 5;
    } else {
      // Any other combination unsupported:
      log('pathfinder: Invalid case', originOnPrimary, destinationOnPrimary, originIsChild, destIsChild, originRouteId, destRouteId);
      return false;
    }
    log('caseNumber', caseNumber);

    // ────────────────────────────────────────────────────────────────────────────────
    // 5) Build the initial onPath segments, based solely on origin/destination/case.
    //    We push each onPath segment into an array “segments.”
    // ────────────────────────────────────────────────────────────────────────────────
    const segments = [];

    function addSegment(routeIdX, startId, endId, isOnPath, isRequired = false) {
      const seg = getSegmentAlongRoute(workingData, routeToIndexMap, routeIdX, startId, endId, isOnPath, isRequired);
      if (seg) segments.push(seg);
    }

    if (caseNumber === 1) {
      // Case 1: Both on primary → single onPath segment:
      addSegment(routeId, originId, destinationId, true);

    } else if (caseNumber === 2) {
      // Case 2: origin on child A, destination on primary
      //  → (a) child A: origin → joinA
      //  → (b) primary: joinA → destination
      const childA      = workingData.routes[originRouteId];
      const joinA       = childA.places[childA.places.length - 1].id;
      addSegment(originRouteId, originId, joinA, true, true);
      addSegment(routeId, joinA, destinationId, true);

    } else if (caseNumber === 3) {
      // Case 3: origin on primary, destination on child B
      //  → (a) primary: origin → splitB
      //  → (b) child B: splitB → destination
      const childB      = workingData.routes[destRouteId];
      const splitB      = childB.places[0].id;
      addSegment(routeId, originId, splitB, true);
      addSegment(destRouteId, splitB, destinationId, true, true);

    } else if (caseNumber === 4) {
      // Case 4: origin on child A, destination on child B (A ≠ B)
      //  → (a) child A: origin → joinA
      //  → (b) primary: joinA → splitB
      //  → (c) child B: splitB → destination
      const childA      = workingData.routes[originRouteId];
      const joinA       = childA.places[childA.places.length - 1].id;
      const childB      = workingData.routes[destRouteId];
      const splitB      = childB.places[0].id;
      addSegment(originRouteId, originId, joinA, true, true);
      addSegment(routeId, joinA, splitB, true);
      addSegment(destRouteId, splitB, destinationId, true, true);

    } else if (caseNumber === 5) {
      // Case 5: origin & destination on the SAME child C
      addSegment(originRouteId, originId, destinationId, true, true);
    }

    // ────────────────────────────────────────────────────────────────────────────────
    // 6) Find child routes fully passed by any onPath primary segments → add as offPath.
    // ────────────────────────────────────────────────────────────────────────────────
    const slugToRouteId = {};
    Object.values(workingData.routes).forEach(r => {
      slugToRouteId[r.slug] = r.id;
    });

    const addedChildOffPaths = new Set();
    segments.forEach(seg => {
      if (!seg.onPath || seg.routeId !== routeId) return;
      // Count intersections (child slugs) in seg.places:
      const slugCounts = {};
      seg.places.forEach(p => {
        if (p.intersect) {
          slugCounts[p.intersect] = (slugCounts[p.intersect] || 0) + 1;
        }
      });
      Object.keys(slugCounts).forEach(slug => {
        if (slugCounts[slug] === 2) {
          const childId = slugToRouteId[slug];
          if (!childId || addedChildOffPaths.has(childId)) return;
          const childRoute = workingData.routes[childId];
          const splitId    = childRoute.places[0].id;
          const joinId     = childRoute.places[childRoute.places.length - 1].id;
          addSegment(childId, splitId, joinId, false);
          addedChildOffPaths.add(childId);
        }
      });
    });

    // ────────────────────────────────────────────────────────────────────────────────
    // 7) Integrate via[]: any childId in via[] must be marked onPath=true,
    //    even if previously offPath=false.  And the primary segment that
    //    overlaps that child must be split into three parts.
    // ────────────────────────────────────────────────────────────────────────────────
    via.forEach(childId => {
      if (!primaryChildren[childId]) return; // skip if not a child of primary
      // 7a) Find the existing child segment (it might have been added offPath in step 6,
      //     or might already be onPath if it was required by case 2/3/4/5).
      let childSegIndex = segments.findIndex(s => s.routeId === childId);
      // If not found at all, drop it (no intersections passed) – invalid via.
      if (childSegIndex < 0) return;
      // Mark that child segment onPath=true:
      segments[childSegIndex].onPath = true;

      // 7b) Find that child’s splitId and joinId:
      const childRoute = workingData.routes[childId];
      const splitId    = childRoute.places[0].id;
      const joinId     = childRoute.places[childRoute.places.length - 1].id;

      // 7c) Find the single primary route segment that contains BOTH splitId and joinId:
      const primarySegIndex = segments.findIndex(s => {
        if (!s.onPath || s.routeId !== routeId) return false;
        const ids = s.places.map(p => p.id);
        return ids.includes(splitId) && ids.includes(joinId);
      });
      if (primarySegIndex < 0) return; // should not happen if the child was offPath found in step 6

      // 7d) Replace that primary segment with three new ones:
      const oldPrimarySeg = segments[primarySegIndex];
      const origPlaces    = oldPrimarySeg.places;

      // (i) Part A: from original start to splitId → onPath=true
      const partA = getSegmentAlongRoute(
        workingData, routeToIndexMap,
        routeId,
        origPlaces[0].id,
        splitId,
        true
      );
      // (ii) Part B: from splitId → joinId → onPath=false
      const partB = getSegmentAlongRoute(
        workingData, routeToIndexMap,
        routeId,
        splitId,
        joinId,
        false
      );
      // (iii) Part C: from joinId → original end → onPath=true
      const partC = getSegmentAlongRoute(
        workingData, routeToIndexMap,
        routeId,
        joinId,
        origPlaces[origPlaces.length - 1].id,
        true
      );

      // Remove the old segment and splice in the three new segments at its position:
      segments.splice(primarySegIndex, 1,
        partA, partB, partC
      );
    });

    // ────────────────────────────────────────────────────────────────────────────────
    // 8) Sort all segments in the order the user “walks” along the primary route:
    //    Each segment’s “sortKey” is the index on the primary route of its first place.
    //    Within equal sortKeys, put onPath=true before onPath=false.
    // ────────────────────────────────────────────────────────────────────────────────
    const primaryIndexMap = routeToIndexMap[routeId];
    segments.sort((a, b) => {
      const aFirstId = a.places[0].id;
      const bFirstId = b.places[0].id;
      // Both aFirstId and bFirstId must be intersections (if not on primary) or normal places on primary.
      const aKey = primaryIndexMap[aFirstId];
      const bKey = primaryIndexMap[bFirstId];

      if (aKey !== bKey) return aKey - bKey;
      // If same first‐index, prefer onPath=true:
      if (a.onPath && !b.onPath) return -1;
      if (!a.onPath && b.onPath) return +1;
      return 0;
    });

    return segments;
}
window.pathfinder = pathfinder;


  // ────────────────────────────────────────────────────────────────────────────────
  // Helper: Build routeId → (placeId → index) maps for fast lookups.
  // ────────────────────────────────────────────────────────────────────────────────
  function buildIndexMaps(workingData) {
    const maps = {};
    Object.keys(workingData.routes).forEach(rid => {
      const arr   = workingData.routes[rid].places;
      const imap  = {};
      for (let i = 0; i < arr.length; i++) {
        imap[arr[i].id] = i;
      }
      maps[rid] = imap;
    });
    // log('buildIndexMaps: maps', maps);
    return maps;
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Helper: Return the routeId (number) that contains placeId, or null if not found.
  // ────────────────────────────────────────────────────────────────────────────────
  
  /**
   * Finds which route (the given route or one of its direct children) contains placeId.
   * Returns the routeId (number) if found, or null if not found.
   * 
   * @param {Object} workingData - The main data with routes.
   * @param {number|string} routeId - The base route to search (and its direct children).
   * @param {Object} indexMaps - Maps for route-place lookup.
   * @param {number} placeId - The place to search for.
   * @returns {number|null} - routeId of the containing route or null.
   */
  function findRouteOfPlace(workingData, routeId, indexMaps, placeId) {
    // Check if the main route contains the place
    const route = workingData.routes[routeId];
    if (route && route.places.some(p => p.id === placeId)) {
      return Number(routeId);
    }
    // Check direct children of the route
    const children = Object.values(workingData.routes).filter(r => r.parent === routeId);
    for (const child of children) {
      const childIndexMap = indexMaps[child.id];
      if (childIndexMap && childIndexMap[placeId] !== undefined) {
        return Number(child.id);
      }
    }
    // Not found in this route or its direct children
    return null;
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Helper: Checks if a given placeId on routeId is an intersection.
  // ────────────────────────────────────────────────────────────────────────────────
  function isIntersection(workingData, routeIdX, placeId) {
    const idx = workingData.routes[routeIdX].places.findIndex(p => p.id === placeId);
    return (idx !== -1 && workingData.routes[routeIdX].places[idx].type === 'intersection');
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Helper: Extracts a segment from “startPlaceId” to “endPlaceId” on route routeIdX,
  //   assuming both appear in forward order (iEnd > iStart).  Sums distance/ascent/descent.
  //
  //   @returns {Object|null}:
  //     { routeId, onPath, distance, ascent, descent, places: [...] }
  // ────────────────────────────────────────────────────────────────────────────────
  function getSegmentAlongRoute(workingData, indexMaps, routeIdX, startPlaceId, endPlaceId, isOnPath, isRequired = false) {
    const route   = workingData.routes[routeIdX];
    const idxMap  = indexMaps[routeIdX];
    const iStart  = idxMap[startPlaceId];
    const iEnd    = idxMap[endPlaceId];
    if (iStart === undefined || iEnd === undefined || iEnd <= iStart) return null;

    // Slice out the subarray in walking order:
    const sliceOfPlaces = route.places.slice(iStart, iEnd + 1);

    // Sum up distance/ascent/descent between consecutive pairs:
    let totalDist = 0, totalAsc = 0, totalDesc = 0;
    for (let i = 1; i < sliceOfPlaces.length; i++) {
      const prev = sliceOfPlaces[i - 1];
      const curr = sliceOfPlaces[i];
      totalDist += Math.abs(curr.dist - prev.dist);
      totalAsc  += Math.max(0, prev.asc  - curr.asc);
      totalDesc += Math.max(0, prev.desc - curr.desc);
    }

    return {
        routeId:  routeIdX,
        onPath:   isOnPath,
        isRequired: isRequired,
        isChildRoute: hasKey(route, 'parent'),
        distance: totalDist,
        ascent:   totalAsc,
        descent:  totalDesc,
        places:   sliceOfPlaces
    };
  }
